/**
 * America/Chicago wall-clock math.
 *
 * The eleven tablets are cheap kiosks. Their own timezone setting is whatever
 * the last person to factory-reset one happened to pick, and at least one of
 * them is sitting in UTC right now. So nothing in this file — and nothing
 * downstream of it — is allowed to ask the device what time it thinks it is.
 * Every conversion runs through `Intl` with an explicit `timeZone`, which
 * carries the real IANA rules, including both DST transitions.
 *
 * The unit the schedule speaks in is `Minutes`: minutes since local midnight.
 * 8:10am is 490, always, in March and in November alike. Absolute epoch ms is
 * what the scheduler fires on; minutes is what a teacher reads off the wall.
 */

const TIME_ZONE = 'America/Chicago';

/** Minutes in a day. Chicago days are 23 or 25 hours long twice a year; this is
 *  the wall-clock count, which is what bands are written in. */
const MINUTES_IN_DAY = 1440;

export interface ChicagoParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0=Sun … 6=Sat, in Chicago. */
  weekday: number;
  /** Minutes since local midnight. 8:10am = 490. */
  minutes: number;
}

export interface ClockDisplay {
  /** 12-hour, no leading zero: '8:10'. */
  time: string;
  /** 'AM' | 'PM'. */
  ampm: string;
  /** Zero-padded: '07'. */
  seconds: string;
}

/** Built once. Constructing an Intl formatter is the expensive part; formatting
 *  with a cached one is cheap enough for a 250ms tick. */
const chicagoFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function pad2(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function pad4(value: number): string {
  return `${value}`.padStart(4, '0');
}

function isoOf(year: number, month: number, day: number): string {
  return `${pad4(year)}-${pad2(month)}-${pad2(day)}`;
}

function parseISO(dateISO: string): { year: number; month: number; day: number } {
  const [year, month, day] = dateISO.split('-');
  return { year: Number(year ?? 0), month: Number(month ?? 1), day: Number(day ?? 1) };
}

/** The wall-clock reading of `epochMs` in Chicago, as an instant in a fictional
 *  UTC day. Only ever compared against another value built the same way. */
function asPseudoUTC(parts: ChicagoParts): number {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

export function chicagoParts(epochMs: number): ChicagoParts {
  const raw: Record<string, number> = {};
  for (const part of chicagoFormat.formatToParts(epochMs)) {
    if (part.type !== 'literal') raw[part.type] = Number(part.value);
  }
  const year = raw['year'] ?? 1970;
  const month = raw['month'] ?? 1;
  const day = raw['day'] ?? 1;
  // Some ICU builds render midnight as hour 24 rather than 0.
  const hour = (raw['hour'] ?? 0) % 24;
  const minute = raw['minute'] ?? 0;
  const second = raw['second'] ?? 0;
  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
    minutes: hour * 60 + minute,
  };
}

/** 'YYYY-MM-DD' in Chicago. At 11:30pm Chicago it is still today, even though
 *  a UTC device has already rolled over to tomorrow. */
export function chicagoDateISO(epochMs: number): string {
  const parts = chicagoParts(epochMs);
  return isoOf(parts.year, parts.month, parts.day);
}

/** 0=Sun … 6=Sat for a calendar date. Pure arithmetic — no timezone involved,
 *  because a date has no offset until you pin a time to it. */
export function weekdayOfISO(dateISO: string): number {
  const { year, month, day } = parseISO(dateISO);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/**
 * Inverse of `chicagoParts`: local wall-clock minutes on a Chicago date, back
 * to an absolute instant.
 *
 * There is no direct API for this, so: guess the instant that would read this
 * way if Chicago were UTC, format the guess back in Chicago, and shove it by
 * however far off the reading was. One pass fixes the standard offset; the
 * second fixes the case where that shove crossed a DST boundary. Converges for
 * every wall-clock time that actually exists.
 *
 * On the spring-forward morning, 2:00–2:59 never happens. Rather than invent an
 * instant, this settles on the real one an hour earlier (2:30 → 1:30 CST). No
 * school bell lives there, and a bell that never rings beats a bell that rings
 * in a hour that does not exist.
 */
export function chicagoEpoch(dateISO: string, minutes: number): number {
  const { year, month, day } = parseISO(dateISO);
  const target = Date.UTC(year, month - 1, day) + minutes * 60_000;
  let guess = target;
  for (let pass = 0; pass < 2; pass += 1) {
    const drift = target - asPseudoUTC(chicagoParts(guess));
    if (drift === 0) break;
    guess += drift;
  }
  return guess;
}

/** Calendar arithmetic on the date string. Days, not 24-hour blocks — the day
 *  after the fall-back Sunday is the Monday, even though 25 hours passed. */
export function addDaysISO(dateISO: string, days: number): string {
  const { year, month, day } = parseISO(dateISO);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return isoOf(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/** For display: 12-hour, no leading zero. 490 -> '8:10', 900 -> '3:00'. */
export function formatMinutes(minutes: number): string {
  const wrapped = ((Math.round(minutes) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const hour24 = Math.floor(wrapped / 60);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${pad2(wrapped % 60)}`;
}

/** The big clock on the kiosk, split so the seconds can be styled small. */
export function formatClock(epochMs: number): ClockDisplay {
  const parts = chicagoParts(epochMs);
  return {
    time: formatMinutes(parts.minutes),
    ampm: parts.hour < 12 ? 'AM' : 'PM',
    seconds: pad2(parts.second),
  };
}
